import { useState, useMemo } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { VersionStamp } from '../../components/VersionStamp'
import { Capacitor } from '@capacitor/core'
import { useT } from '../../i18n'

interface Props {
  onBack: () => void
}

const SUPPORT_EMAIL = 'talk@jordandraper.com'

/**
 * FAQ copy is built per-render so the subscription answer can adapt
 * to where the user's billing relationship actually lives. On the iOS
 * native shell, subscriptions go through Apple StoreKit and are
 * managed in iOS Settings — Stripe's customer portal isn't reachable
 * from the App Store build, so mentioning it would be wrong. On web
 * (and Android, until that ships), Stripe is the source of truth.
 */
function buildFaq(isIosNative: boolean, t: (k: string) => string): Array<{ q: string; a: string }> {
  return [
    {
      q: t('How does the All-Access subscription work?'),
      a: isIosNative
        ? t('It’s a monthly or yearly subscription that auto-renews. Manage or cancel anytime from your profile in the app, or from your iPhone’s Settings → Apple ID → Subscriptions if you subscribed through the App Store.')
        : t('It’s a monthly or yearly subscription that auto-renews. Cancel anytime from your profile. Manage opens a billing page where you can cancel, update card, or switch plans.'),
    },
    {
      q: t('Can I play without subscribing?'),
      a: t('Yes, guests can read every weekly story, vote, and join a hosted room with a code. Subscribing unlocks hosting your own rooms, submitting to weekly events, and the AI opponent personas.'),
    },
    {
      q: t('How do weekly events work?'),
      a: t('New weekly events drop every Monday morning ET, one per supported game. I’d Eat That gives everyone three shared ingredients plus one random per player and you write a 3-course dish across the week. Colorful posts six subjects and you pick the colour that fits each. The week locks the following Monday and trophies get awarded for top stories and crowd-pick majorities.'),
    },
    {
      q: t('I lost my password.'),
      a: t('On the sign-in screen, tap "Forgot password?" and we’ll email you a reset link. The link opens the app and lets you set a new one.'),
    },
    {
      q: t('I want to stop playing. What happens to my account?'),
      a: 'Profile → Privacy → Delete my account closes your login. We keep your submitted weekly stories and Colorful picks in the archive (other people voted on them and the leaderboards depend on them) under your chosen display name. Your profile photo is removed. If you have an active subscription you’ll be asked to cancel it first so you don’t keep getting billed.',
    },
  ]
}

export function HelpScreen({ onBack }: Props) {
  const t = useT()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  // Capacitor.getPlatform() returns 'web' / 'ios' / 'android'. Memoised
  // because it can't change inside a session — the runtime is fixed.
  const isIosNative = useMemo(
    () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios',
    [],
  )
  const FAQ = useMemo(() => buildFaq(isIosNative, t), [isIosNative])

  return (
    <div className="screen pb-12">
      <PageHeader title={t('Help & Support')} onBack={onBack} />

      <div className="px-5 py-5 space-y-6 overflow-y-auto">
        {/* Direct contact */}
        <section>
          <h2 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Get in touch')}
          </h2>
          <div className="card p-4 space-y-3">
            <p className="text-sm text-brand-text">{t('Bug reports, refund requests, or just feedback: email us and we’ll get back within a couple days.')}</p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=JDG%20app%20feedback`}
              className="inline-block px-4 py-2 rounded-xl bg-brand-text text-white
                         text-sm font-semibold active:scale-95 transition-transform"
            >
              Email {SUPPORT_EMAIL}
            </a>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Common questions')}
          </h2>
          <div className="space-y-2">
            {FAQ.map((item, i) => {
              const open = openIndex === i
              return (
                <div key={i} className="card overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenIndex(open ? null : i)}
                    className="w-full p-4 flex items-center gap-3 text-left
                               active:bg-brand-elevated transition-colors"
                  >
                    <span className="flex-1 font-semibold text-brand-text">{item.q}</span>
                    <span className={`text-brand-muted transition-transform ${open ? 'rotate-90' : ''}`}>
                      ›
                    </span>
                  </button>
                  {open && (
                    <div className="px-4 pb-4 text-sm text-brand-text leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* Useful links */}
        <section>
          <h2 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Links')}
          </h2>
          <div className="space-y-2">
            <ExternalLink href="https://jordandraper.com" label={t('Jordan Draper Games, main site')} />
            <ExternalLink
              href={`mailto:${SUPPORT_EMAIL}?subject=JDG%20app%20bug%20report`}
              label={t('Report a bug')}
            />
          </div>
        </section>

        {/* Version — real identity of this install, not a hand-kept string */}
        <div className="pt-2">
          <VersionStamp />
        </div>
      </div>
    </div>
  )
}

function ExternalLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="card p-4 flex items-center gap-3 active:bg-brand-elevated transition-colors"
    >
      <span className="flex-1 font-medium text-brand-text">{label}</span>
      <span className="text-brand-muted">↗</span>
    </a>
  )
}
